// src/security/capacidades.js

/**
 * Matriz de capacidades — o ÚNICO lugar que decide o que cada usuário pode ver e
 * fazer DENTRO das páginas, a partir do plano da conta e dos papéis do usuário.
 *
 * Três camadas, cada uma com um dono:
 *   1. PÁGINAS: o menu da configuração no IOT (item de menu + papéis permitidos).
 *      Rota sem item de menu não existe para a conta (ver InnerRoutes).
 *   2. CAPACIDADES (este arquivo): o que aparece ou é permitido dentro da página.
 *   3. PERFIL DO PLANO (perfisPlano.js): COMO a tela se apresenta para um plano —
 *      não é "pode ou não pode".
 *
 * As telas só perguntam `pode('chave')` (usePermissao) ou usam <Permitido cap>.
 * Nenhuma tela conhece plano ou papel. Plano novo ou regra nova = mexer aqui.
 *
 * É visibilidade de interface. Quem PROTEGE o dado é o SecurityConfig do
 * crm-back; uma regra de escrita nova precisa existir lá também.
 *
 * Regra de cada capacidade (todos os campos são opcionais):
 *   planos             planos que têm a capacidade. Ausente = qualquer plano.
 *                      MASTER (plano interno da AUFTEK) tem todas as de plano.
 *   papeis             papéis que têm a capacidade (basta um). Ausente = todos.
 *   exceto             papéis que NUNCA têm a capacidade, mesmo cumprindo o resto.
 *   adminQualquerPlano o ADMIN (equipe da AUFTEK) passa pelo filtro de plano.
 *                      Opt-in: liberar ADMIN em tudo abriria de uma vez o que é
 *                      restrito por plano para o cliente, que é outra decisão.
 *
 * Não há hierarquia implícita de papéis: ADMIN não herda de GESTOR. Quem deve
 * ter a capacidade aparece escrito na lista.
 */

export const PLANO_MASTER = 'MASTER';
export const PLANO_PADRAO = 'BASIC';

/** Planos com os recursos analíticos da amostra (laboratório, IA, diluição). */
const ANALITICOS = ['ETA', 'ETE', 'SANEAMENTO', 'LEITE'];

/** Planos com a tela de gráficos completa; os demais veem só o resumo. */
const GRAFICOS_COMPLETOS = ['SANEAMENTO', 'LEITE'];

export const CAPACIDADES = Object.freeze({
    // ── Geral ──
    /** Qualquer ação que altere dado. O visitante (GUEST) só consulta. */
    'dados.alterar': { exceto: ['GUEST'] },

    /** Lixeira: listar amostras e análises excluídas. */
    'lixeira.ver': { papeis: ['ADMIN'] },
    /** Desfazer a exclusão de um experimento (o backend também exige ADMIN). */
    'lixeira.restaurar': { papeis: ['ADMIN'] },

    // ── Amostras (card do experimento) ──
    /** Resultados por bactéria no card (predição da IA e laboratório). */
    'amostra.resultados': { planos: ANALITICOS },
    'amostra.resultadoLab': { planos: ANALITICOS },
    'amostra.processarIA': { planos: ANALITICOS },
    'amostra.diluicao': { planos: ANALITICOS },
    'amostra.excluir': { planos: ANALITICOS },
    /** Só o plano MASTER: nenhum plano de cliente usa os dados da amostra. */
    'amostra.dadosAmostra': { planos: [] },
    /** Label de treinamento do modelo: curadoria interna da AUFTEK. */
    'amostra.rotuloTreino': { planos: ANALITICOS, papeis: ['ADMIN'] },
    'amostra.forcarEncerramento': { papeis: ['ADMIN'] },

    // ── Gráficos do experimento ──
    'graficos.completos': { planos: GRAFICOS_COMPLETOS, adminQualquerPlano: true },
    /**
     * Abas de funcionamento da torre (Peltier, fonte, frequências, malhas de
     * controle): diagnóstico de hardware, trabalho da AUFTEK.
     */
    'graficos.parametrosTorre': { papeis: ['ADMIN'] },

    // ── Análises ──
    'analise.normalizarDia': { papeis: ['ADMIN'] },
    'analise.criarMock': { papeis: ['ADMIN'] },
    'analise.verMocks': { papeis: ['ADMIN'] },
    /** Detalhes técnicos do resultado já abertos no modal (quem dá suporte). */
    'analise.detalhesTecnicosAbertos': { papeis: ['ADMIN'] },

    // ── Dispositivos ──
    /**
     * Dados ao vivo no card, indicador de conexão e tela de Telemetria. O ADMIN
     * vê em qualquer plano: é quem dá suporte e diagnostica o hardware.
     */
    'telemetria.ver': { planos: ANALITICOS, adminQualquerPlano: true },
    'dispositivo.diluicaoPadrao': { papeis: ['ADMIN', 'GESTOR'] },
    /** Painéis de engenharia do dashboard do dispositivo. */
    'dispositivo.engenharia': { papeis: ['ADMIN', 'LAB'] },

    // ── Conta ──
    'estacao.limiteRegulatorio': { papeis: ['ADMIN', 'GESTOR'] },
    'empresa.editar': { papeis: ['ADMIN', 'GESTOR'] },
    /**
     * Completar o cadastro da conta pendente de validação. Quem não pode vê a
     * tela de espera até o gestor concluir.
     */
    'cliente.configurarCadastro': { papeis: ['ADMIN', 'GESTOR'] },

    // ── Páginas de coleta (leite, colostro, água) ──
    /** Qual análise do protocolo cada página mostra: config da AUFTEK, antes do uso. */
    'coleta.configurarPagina': { papeis: ['ADMIN'] },
    /** Cadastro de pontos de coleta da propriedade. */
    'pontoColeta.gerenciar': { papeis: ['ADMIN', 'GESTOR'] },
    /**
     * Referências da página: o que aparece no gráfico, as metas da propriedade e qual
     * referência o assistente usa. Vale para a conta inteira; os valores da legislação
     * vêm da norma e ninguém edita.
     */
    'coleta.referencias': { papeis: ['ADMIN', 'GESTOR'] },
    /** Excluir uma amostra da página (DELETE /amostras é só ADMIN/GESTOR no backend). */
    'coleta.excluir': { papeis: ['ADMIN', 'GESTOR'] },
    /**
     * Ver o laboratório ao lado do modelo no detalhe da amostra: é a validação do
     * modelo, trabalho interno da AUFTEK. Quem lança o resultado continua lançando
     * pelo menu da amostra.
     */
    'coleta.compararLaboratorio': { papeis: ['ADMIN'] },
    /**
     * Plano de ações (plano LEITE): quem está no tambo registra a ação e a conclusão.
     * COLETOR só vê. Excluir (desativar) fica com ADMIN e GESTOR, como nas amostras.
     */
    'planoAcao.gerenciar': { papeis: ['ADMIN', 'GESTOR', 'LAB'] },
    'planoAcao.excluir': { papeis: ['ADMIN', 'GESTOR'] },
});

/**
 * Avalia uma capacidade para um plano e um conjunto de papéis.
 *
 * Chave desconhecida NEGA: um erro de digitação esconde a funcionalidade em vez
 * de liberá-la. O teste da matriz garante que toda chave usada no código existe.
 */
export function avaliarCapacidade(chave, { plano, papeis }) {
    const regra = CAPACIDADES[chave];
    if (!regra) {
        if (process.env.NODE_ENV !== 'production') {
            console.error(`[permissões] capacidade desconhecida: "${chave}"`);
        }
        return false;
    }

    const temAlgum = (lista) => lista.some((p) => papeis.includes(p));

    if (regra.exceto && temAlgum(regra.exceto)) return false;
    if (regra.papeis && !temAlgum(regra.papeis)) return false;

    if (!regra.planos) return true;
    if (plano === PLANO_MASTER) return true;
    if (regra.adminQualquerPlano && papeis.includes('ADMIN')) return true;
    return regra.planos.includes(plano);
}
